import React from 'react';
import { Route } from 'react-router-dom';
import { HomepageCompositionRoot } from '@backstage/plugin-home';
import { apiDocsPlugin, ApiExplorerPage } from '@backstage/plugin-api-docs';
import {
  CatalogEntityPage,
  CatalogIndexPage,
  catalogPlugin,
} from '@backstage/plugin-catalog';
import {
  CatalogImportPage,
  catalogImportPlugin,
} from '@backstage/plugin-catalog-import';
import { EntityGithubActionsContent } from '@backstage-community/plugin-github-actions';
import { TechRadarPage } from '@backstage-community/plugin-tech-radar';
import { ScaffolderPage, scaffolderPlugin } from '@backstage/plugin-scaffolder';
import { ScaffolderFieldExtensions } from '@backstage/plugin-scaffolder-react';
import { CustomAppNameFieldExtension } from './components/custom_components';
import { CustomProjectNameFieldExtension } from './components/custom_components';
import { CustomUserFieldExtension } from './components/custom_components';
import { CustomModelNameFieldExtension } from './components/custom_components';
import { CustomRepoSetupFieldExtension } from './components/custom_components';
import { CustomProjectManagerFieldExtension } from './components/custom_components';
import { CustomGitHubTeamsFieldExtension } from './components/custom_components';
import { CustomAutoGrowTextareaFieldExtension } from './components/custom_components';
import { CustomOwnerGroupFieldExtension } from './components/custom_components';
import { orgPlugin } from '@backstage/plugin-org';
import { SearchPage } from '@backstage/plugin-search';
import {
  techdocsPlugin,
  TechDocsIndexPage,
  TechDocsReaderPage,
} from '@backstage/plugin-techdocs';
import { TechDocsAddons } from '@backstage/plugin-techdocs-react';
import { ReportIssue } from '@backstage/plugin-techdocs-module-addons-contrib';
import { TechDocsSidebarStyleAddon } from './components/techdocs/TechDocsSidebarStyleAddon';
import { DocumentationOnlyTechDocsHome } from './components/techdocs/DocumentationOnlyTechDocsHome';
import { UserSettingsPage, ProviderSettingsItem } from '@backstage/plugin-user-settings';
import { apis } from './apis';
import { entityPage } from './components/catalog/EntityPage';
import { focusCatalogColumns } from './components/catalog/focusCatalogColumns';
import { searchPage } from './components/search/SearchPage';
import { Root } from './components/Root';
import { UnifiedThemeProvider } from "@backstage/theme";
import {
  AlertDisplay,
  GitHubIcon,
  OAuthRequestDialog,
  SignInPage,
} from '@backstage/core-components';
import { microsoftAuthApiRef, githubAuthApiRef } from '@backstage/core-plugin-api';
import { createApp } from '@backstage/app-defaults';
import { AppRouter, FlatRoutes } from '@backstage/core-app-api';
import { CatalogGraphPage } from '@backstage/plugin-catalog-graph';
import { RequirePermission } from '@backstage/plugin-permission-react';
import { catalogEntityCreatePermission } from '@backstage/plugin-catalog-common/alpha';
import { HomePage } from './components/custom_pages/HomePage';
import { myTheme } from './theme/myTheme';
import { NotificationsPage } from '@backstage/plugin-notifications';
import { TasksPage } from '@internal/backstage-plugin-tasks';
import { DeploymentPage } from '@internal/backstage-plugin-deployment';
import { AwsCostExplorerPage } from '@internal/backstage-plugin-aws-cost-explorer';
import { costInsightsAwsPlugin } from '@aws/cost-insights-plugin-for-backstage';
import { AutoGenDocPage } from '@internal/backstage-plugin-autogen-doc';
import { RequestTrackerPage } from '@internal/backstage-plugin-request-tracker';

const app = createApp({
  apis,
  bindRoutes({ bind }) {
    bind(catalogPlugin.externalRoutes, {
      createComponent: scaffolderPlugin.routes.root,
      viewTechDoc: techdocsPlugin.routes.docRoot,
      createFromTemplate: scaffolderPlugin.routes.selectedTemplate,
    });
    bind(apiDocsPlugin.externalRoutes, {
      registerApi: catalogImportPlugin.routes.importPage,
    });
    bind(scaffolderPlugin.externalRoutes, {
      registerComponent: catalogImportPlugin.routes.importPage,
      viewTechDoc: techdocsPlugin.routes.docRoot,
    });
    bind(orgPlugin.externalRoutes, {
      catalogIndex: catalogPlugin.routes.catalogIndex,
    });
  },
  plugins: [costInsightsAwsPlugin],
  components: {
    SignInPage: props => (
      <SignInPage
        {...props}
        auto
        provider={{
          id: 'microsoft-auth-provider',
          title: 'Azure Auth',
          message: 'Sign in using Azure',
          apiRef: microsoftAuthApiRef,
        }}
      />
    ),
  },
  themes: [{
    id: 'my-theme',
    title: 'My Custom Theme',
    variant: 'light',
    Provider: ({ children }) => (
      <UnifiedThemeProvider theme={myTheme} children={children} />
    ),
  }],
});

const routes = (
  <FlatRoutes>
    <Route path="/" element={<HomepageCompositionRoot />}>
      <HomePage />
    </Route>
    <Route
      path="/catalog"
      element={<CatalogIndexPage columns={focusCatalogColumns} />}
    />
    <Route
      path="/catalog/:namespace/:kind/:name"
      element={<CatalogEntityPage />}
    >
      {entityPage}
    </Route>
    <Route path="/docs" element={<TechDocsIndexPage />}>
      <DocumentationOnlyTechDocsHome />
    </Route>
    <Route
      path="/tech-radar"
      element={<TechRadarPage width={1500} height={800} />}
    />
    <Route
      path="/github-actions"
      element={<EntityGithubActionsContent />}
    />
    <Route
      path="/docs/:namespace/:kind/:name/*"
      element={<TechDocsReaderPage />}
    >
      <TechDocsAddons>
        <TechDocsSidebarStyleAddon />
        <ReportIssue />
      </TechDocsAddons>
    </Route>
    <Route path="/devkits"
      element={
        <ScaffolderPage
          headerOptions={{
            pageTitleOverride: "DevKits",
            title: 'DevKits',
            subtitle: 'Initiate new repositories within our organization using DevKits. Each repository comes preconfigured with everything needed to start a project, including starter files, clear documentation, automated testing and deployment tools, built-in security and quality checks, and easy ways to track costs and resources.',
          }}
          templateFilter={entity => {
            return entity.spec?.type === 'service' || entity.spec?.type === 'website';
          }}
          defaultPreviewTemplate="Other DevKits"
          groups={[
            {
              title: "All DevKits",
              filter: (entity) => {
                return entity.spec?.type === 'service' || entity.spec?.type === 'website';
              }
            },
          ]}
        >
          <ScaffolderFieldExtensions>
            <CustomAppNameFieldExtension />
            <CustomProjectNameFieldExtension />
            <CustomUserFieldExtension />
            <CustomProjectManagerFieldExtension />
            <CustomModelNameFieldExtension />
            <CustomRepoSetupFieldExtension />
            <CustomGitHubTeamsFieldExtension />
            <CustomAutoGrowTextareaFieldExtension />
            <CustomOwnerGroupFieldExtension />
          </ScaffolderFieldExtensions>
        </ScaffolderPage>
      }
    />
    <Route path="/requests"
      element={
        <ScaffolderPage
          headerOptions={{
            pageTitleOverride: "Requests",
            title: 'Requests',
            subtitle: 'If you need any kind of resources or support, please submit a request using the requirement-specific form provided. This will help your request progress smoothly through any necessary approvals and ensure timely assistance.',
          }}
          templateFilter={entity => {
            return entity.spec?.type === 'automation' || entity.spec?.type === 'request';
          }}
          groups={[
            {
              title: "All Requests",
              filter: (entity) => {
                return entity.spec?.type === 'automation' || entity.spec?.type === 'request';
              }
            }
          ]}
        >
          <ScaffolderFieldExtensions>
            <CustomAppNameFieldExtension />
            <CustomProjectNameFieldExtension />
            <CustomUserFieldExtension />
            <CustomProjectManagerFieldExtension />
            <CustomModelNameFieldExtension />
            <CustomRepoSetupFieldExtension />
            <CustomGitHubTeamsFieldExtension />
            <CustomAutoGrowTextareaFieldExtension />
            <CustomOwnerGroupFieldExtension />
          </ScaffolderFieldExtensions>
        </ScaffolderPage>
      }
    />
    <Route path="/api-docs" element={<ApiExplorerPage />} />
    <Route
      path="/catalog-import"
      element={
        <RequirePermission permission={catalogEntityCreatePermission}>
          <CatalogImportPage />
        </RequirePermission>
      }
    />
    <Route path="/search" element={<SearchPage />}>
      {searchPage}
    </Route>
    <Route path="/settings" element={
      <UserSettingsPage
        providerSettings={
          <ProviderSettingsItem
            title=""
            description="Provides authentication towards GitHub APIs"
            apiRef={githubAuthApiRef}
            icon={() => <GitHubIcon />}
          />
        }
      />
    } />
    <Route path="/home" element={<HomePage />} />
    <Route path="/notifications" element={<NotificationsPage />} />
    <Route path="/catalog-graph" element={<CatalogGraphPage />} />
    <Route path="/tasks" element={<TasksPage />} />
    <Route path="/deployments" element={<DeploymentPage />} />
    <Route path="/cost-explorer" element={<AwsCostExplorerPage />} />
    <Route path="/auto-gen-doc" element={<AutoGenDocPage />} />
    <Route path="/request-tracking" element={<RequestTrackerPage />} />
  </FlatRoutes>
);

export default app.createRoot(
  <>
    <AlertDisplay />
    <OAuthRequestDialog />
    <AppRouter>
      <Root>{routes}</Root>
    </AppRouter>
  </>,
);
